import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useLicense } from './LicenseContext';
import { getStoredApiKey } from '../utils/apiKey';

interface ValidationState {
  // Authentication
  isAuthenticated: boolean;
  user: any | null;

  // License
  hasValidLicense: boolean;
  licenseLoading: boolean;

  // API Key
  hasValidApiKey: boolean;
  apiKeyLoading: boolean;
  // True when the API key state could not be determined (network/fetch/decrypt
  // failure) — distinct from "the user has not configured a key"
  apiKeyCheckFailed: boolean;

  // Combined state
  canFetchBatches: boolean | null;
  validationError: string | null;

  // Trigger re-validation
  recheckApiKey: () => void;
}

const ValidationContext = createContext<ValidationState | undefined>(undefined);

interface ValidationProviderProps {
  children: ReactNode;
}

export const ValidationProvider: React.FC<ValidationProviderProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const {
    hasValidLicense, loading: licenseLoading, licenseKey, licenseKeyLoading, licenseKeyError,
    isBreweryMember, breweryId,
  } = useLicense();
  const [hasValidApiKey, setHasValidApiKey] = useState(false);
  const [apiKeyLoading, setApiKeyLoading] = useState(true);
  const [apiKeyCheckFailed, setApiKeyCheckFailed] = useState(false);
  const [apiKeyCheckTrigger, setApiKeyCheckTrigger] = useState(0);

  // Check API key validity
  useEffect(() => {
    const checkApiKey = async () => {
      // Brewery members don't need their own API key - data comes from Firestore
      if (isBreweryMember && breweryId) {
        setHasValidApiKey(true);
        setApiKeyCheckFailed(false);
        setApiKeyLoading(false);
        return;
      }

      if (!user?.uid) {
        setHasValidApiKey(false);
        setApiKeyCheckFailed(false);
        setApiKeyLoading(false);
        return;
      }

      // Encryption key still resolving — stay in loading, don't declare anything
      if (licenseKeyLoading) {
        setApiKeyLoading(true);
        return;
      }

      if (!licenseKey) {
        // Either no license (terminal) or the key fetch failed (transient)
        setHasValidApiKey(false);
        setApiKeyCheckFailed(!!licenseKeyError);
        setApiKeyLoading(false);
        return;
      }

      try {
        setApiKeyLoading(true);
        const apiKey = await getStoredApiKey(user.uid, licenseKey);
        console.log('ValidationContext - API key check result:', !!apiKey);
        setHasValidApiKey(!!apiKey);
        setApiKeyCheckFailed(false);
      } catch (err) {
        console.error('Error checking API key:', err);
        setHasValidApiKey(false);
        setApiKeyCheckFailed(true);
      } finally {
        setApiKeyLoading(false);
      }
    };

    checkApiKey();
  }, [user?.uid, licenseKey, licenseKeyLoading, licenseKeyError, apiKeyCheckTrigger, isBreweryMember, breweryId]);

  // Function to trigger API key re-check
  const recheckApiKey = () => {
    console.log('ValidationContext - Triggering API key recheck');
    setApiKeyCheckTrigger(prev => prev + 1);
  };

  // Combined validation state
  // Brewery members: valid if they have a breweryId (license comes from admin)
  const isBreweryUser = isBreweryMember && breweryId;
  const effectiveLicense = isBreweryUser ? true : hasValidLicense;
  const effectiveApiKey = isBreweryUser ? true : hasValidApiKey;
  const effectiveLicenseLoading = isBreweryUser ? false : licenseLoading;
  const effectiveApiKeyLoading = isBreweryUser ? false : apiKeyLoading;

  const canFetchBatches = user && effectiveLicense && effectiveApiKey && !authLoading && !effectiveLicenseLoading && !effectiveApiKeyLoading;

  let validationError: string | null = null;

  if (!user && !authLoading) {
    validationError = 'User not authenticated';
  } else if (user && !effectiveLicense && !effectiveLicenseLoading) {
    validationError = 'No active license';
  } else if (user && effectiveLicense && !effectiveApiKey && !effectiveApiKeyLoading) {
    validationError = apiKeyCheckFailed
      ? 'Could not verify API key'
      : 'API key not configured';
  }

  const value: ValidationState = {
    isAuthenticated: !!user,
    user,
    hasValidLicense: effectiveLicense,
    licenseLoading: effectiveLicenseLoading,
    hasValidApiKey: effectiveApiKey,
    apiKeyLoading: effectiveApiKeyLoading,
    apiKeyCheckFailed,
    canFetchBatches,
    validationError,
    recheckApiKey,
  };

  return (
    <ValidationContext.Provider value={value}>
      {children}
    </ValidationContext.Provider>
  );
};

export const useValidation = (): ValidationState => {
  const context = useContext(ValidationContext);
  if (context === undefined) {
    throw new Error('useValidation must be used within a ValidationProvider');
  }
  return context;
};
